import FormComponent from '../../../../components/FormComponent';
import FormGenerator, { FormTypes } from '../../../../components/FormGenerator';
import Modal from '../../../../components/Modal';
import SelectorEngine from '../../../../utils/dom/selector-engine';
import EventHandler from '../../../../utils/dom/event-handler';
import { Events } from '../../../../utils/dom/html-events';
import { ClassNames } from '../../../../utils/dom/css-classes';
import { InputTypes, validFormElementValue } from '../../../../utils/validateFormElement';
import { VenueRegistrationQuestionTypes } from '../questionTypes';
import { venueRegistrationQuestionRenderers } from '../questionRenderers';

const DEFAULT_SEARCH_MODAL_SELECTOR = '#js-venue-selection-search-modal';
const CLICK_EVENT = Events.Click || 'click';

const DEFAULT_MANUAL_ENTRY_NOTE_TITLE = 'Please note:';
const DEFAULT_MANUAL_ENTRY_NOTE_DESCRIPTION =
	'The venue that you are adding below will need to be validated by the LTA before you can complete your registration.';
const DEFAULT_MANUAL_ENTRY_NOTE_BACK_LABEL = 'Return to venue search';

const DEFAULT_MULTI_QUESTION = 'Which organisation would you like to complete your registration for?';
const DEFAULT_SINGLE_QUESTION = 'You are completing your registration for this organisation';

/** Defaults for the nested `venueRegistrationVenueSearch` form. */

const DEFAULT_SEARCH_QUESTION = 'Please search for your venue';
const DEFAULT_SEARCH_ERROR_MESSAGE = 'Please search for and select your venue';
const DEFAULT_SEARCH_LABEL = 'Venue name';
const DEFAULT_SEARCH_PLACEHOLDER = 'Please enter a venue name';
const DEFAULT_MANUAL_ADDRESS_LABEL = "Can't find your venue? Enter it manually";
const DEFAULT_MANUAL_ENTRY_TITLE = 'Enter your venue manually';
const DEFAULT_SEARCH_AGAIN_LABEL = 'Search again';
const DEFAULT_SEARCH_AGAIN_ICON = 'search';
const DEFAULT_WEB_ADMIN_SUPPORT_LINK_LABEL = 'LTA Customer Support team';
const DEFAULT_WEB_ADMIN_SUPPORT_LINK_URL = 'https://www.lta.org.uk/about-us/what-we-do/contact-us/';
const DEFAULT_WEB_ADMIN_ERROR_MESSAGE = `This organisation already has a Web Admin assigned or is part of a multi-site operator. Please contact them directly to request access. 
If you are unsure who the Web Admin is or have any further questions, please contact the <a href="${DEFAULT_WEB_ADMIN_SUPPORT_LINK_URL}" class="u-link u-text-lta-blue" target="_blank" rel="noopener noreferrer">${DEFAULT_WEB_ADMIN_SUPPORT_LINK_LABEL}</a>.`;

export const VENUE_SELECTION_EXIT_EVENT = 'venue-selection-exit';

/**
 * Maps venue API objects / radio options into a consistent option shape.
 * @param {Object} data
 * @returns {Array<{id: string, label: string, description: string, value: string}>}
 */
export const normalizeVenueOptions = (data = {}) => {
	if (Array.isArray(data.options) && data.options.length > 0) {
		return data.options.map((option) => ({
			...option,
			id: option.id,
			label: option.label || option.venue || option.name || '',
			description: option.description || formatVenueAddress(option.address) || '',
			value: option.value ?? option.id
		}));
	}

	const venues = data.venues || [];
	return venues.map((venue) => ({
		...venue,
		id: venue.id,
		label: venue.venue || venue.name || venue.label || '',
		description: formatVenueAddress(venue.address) || venue.description || '',
		value: venue.value ?? venue.id
	}));
};

/**
 * Pick the heading copy from venue count.
 * Uses dedicated `multiVenueQuestion` / `singleVenueQuestion` fields so the
 * resolved `question` is never fed back into itself.
 */
export const resolveVenueQuestion = (data = {}, options = []) => {
	if (options.length === 1) {
		return data.singleVenueQuestion || DEFAULT_SINGLE_QUESTION;
	}

	return data.multiVenueQuestion || DEFAULT_MULTI_QUESTION;
};

const formatVenueAddress = (address) => {
	if (!address) {
		return '';
	}
	if (typeof address === 'string') {
		return address;
	}
	const parts = [address.street, address.city, address.state, address.postCode || address.postcode].filter(Boolean);
	return parts.join(', ');
};

const mapSearchResultToOption = (result = {}) => {
	const venueValue = result.value && typeof result.value === 'object' ? result.value : result;
	const id = venueValue.id || venueValue.schoolId || venueValue.venueId;
	const label = venueValue.schoolName || venueValue.venueName || venueValue.name || venueValue.venue || '';
	const description =
		formatVenueAddress(venueValue.address) ||
		[venueValue.county, venueValue.postcode].filter(Boolean).join(', ');

	return {
		id,
		label,
		description,
		value: id
	};
};

const buildOptionValue = (option, data) => {
	if (!option) {
		return null;
	}
	const answerField = data?.answerField || data?.valueField || 'value';
	return {
		...option,
		fieldName: data.fieldName,
		value: option[answerField]
	};
};

/**
 * Venue registration radio selection with card-style venues.
 * - 0 venues: search CTA only
 * - 1 venue: card only (auto-selected) + single-venue title
 * - 2+ venues: radio + cards + multi-venue title
 * Search CTA opens a nested `lta-form-modal` FormGenerator with school search.
 */
class VenueSelection extends FormComponent {
	constructor(element, config) {
		const sourceData = config?.data || {};
		const options = normalizeVenueOptions(sourceData);
		const data = {
			...sourceData,
			options,
			multiVenueQuestion: sourceData.multiVenueQuestion || sourceData.question || DEFAULT_MULTI_QUESTION,
			singleVenueQuestion: sourceData.singleVenueQuestion || DEFAULT_SINGLE_QUESTION
		};
		data.question = resolveVenueQuestion(data, options);

		// Defer first render until initial selection is set (avoids render → updateData → render loop).
		const { data: _ignored, ...restConfig } = config || {};
		super(element, restConfig);

		const valueField = data.valueField || 'value';
		let initialOption = null;
		if (data.value) {
			initialOption = options.find((option) => option[valueField] === data.value) || null;
		} else if (options.length === 1) {
			initialOption = options[0];
		}

		if (initialOption) {
			this._value = buildOptionValue(initialOption, data);
		}

		this.data = data;

		// Publish after `data` is set so `beforeDependentQuestionChange` is wired.
		// Assigning `_dependency` directly skips that callback, and a later setter
		// call then no-ops on the same array — a single auto-selected venue
		// (for example one Multi-site operator) never reveals its follow-up step,
		// so the journey submits after step 1.
		if (this._value?.dependentQuestion?.length) {
			this.dependentQuestion = this._value.dependentQuestion;
		}
	}

	/**
	 * Keep heading in sync when venues are added (e.g. via search).
	 */
	updateData(_data = {}) {
		if (!_data || Object.keys(_data).length === 0) {
			return;
		}

		const nextOptions = _data.options || this.options;
		const merged = {
			...this.data,
			..._data
		};

		if (_data.options) {
			merged.question = resolveVenueQuestion(merged, nextOptions);
		}

		this.data = merged;
	}

	get options() {
		return this.data?.options || [];
	}

	get isMultiSelect() {
		return this.options.length > 1;
	}

	markup(value, data, titleMarkup) {
		this.radioContainerClass = `lta-venue-selection-container-${data.id}`;
		const inputID = `lta-venue-selection-id-${data.id}`;
		const venueCards = this.options.map((option) => this.renderVenueOption(option, data)).join('');
		const labelData = data?.instructionText
			? `<label for=${inputID} class="lta-text-input__label u-text-grey-lta">${data.instructionText}</label>`
			: '';
		const skipTitle = data?.skipButtonTitle || 'Venue not showing? Search here';
		const skipButton =
			data?.isSkipButtonVisible === false
				? ''
				: `<button type="button" class="lta-venue-selection__search-link">${skipTitle}</button>`;

		return `
			<div class="lta-venue-selection">
				<div class="lta-venue-selection__title">${titleMarkup}</div>
				<div class="${this.radioContainerClass} lta-venue-selection__options">
					${labelData}
					<div class="lta-text-input--error"></div>
					${venueCards}
				</div>
				${skipButton}
			</div>
		`;
	}

	renderVenueOption(option, data) {
		const valueField = data?.valueField || 'value';
		const isChecked = option?.[valueField] === this?.value?.[valueField];
		const cardMarkup = `
			<div class="lta-venue-selection__card">
				<div class="lta-venue-selection__card-name">${option.label || ''}</div>
				${option.description ? `<div class="lta-venue-selection__card-address">${option.description}</div>` : ''}
			</div>
		`;

		if (!this.isMultiSelect) {
			return `<div class="lta-venue-selection__option lta-venue-selection__option--single">${cardMarkup}</div>`;
		}

		const radioButtonName = `venue-selection-name-question${data.id}`;
		const radioButtonID = `venue-selection-question-${data.id}-${option.id}`;

		return `
			<div class="lta-venue-selection__option">
				<input
					class="lta-venue-selection__radio"
					role="radio"
					type="radio"
					name="${radioButtonName}"
					id="${radioButtonID}"
					value="${option.id}"
					${isChecked ? 'checked' : ''}
					${data.disable ? 'disabled' : ''}
				/>
				<label class="lta-venue-selection__label" for="${radioButtonID}">
					${cardMarkup}
				</label>
			</div>
		`;
	}

	afterRender() {
		this.radioButtonContainerElement = SelectorEngine.findOne(`.${this.radioContainerClass}`, this.containerEl);
		this.radioButtonElements = SelectorEngine.find('input[type=radio]', this.radioButtonContainerElement) || [];
		this.searchLinkButton = SelectorEngine.findOne('.lta-venue-selection__search-link', this.containerEl);

		if (this.isMultiSelect && this.radioButtonElements.length > 0) {
			this.selectedRadio = this.radioButtonElements.find((radio) => radio.checked);
			for (const radioButton of this.radioButtonElements) {
				EventHandler.on(radioButton, CLICK_EVENT, this.radioButtonClickHandler.bind(this));
			}
		}

		if (this.searchLinkButton) {
			EventHandler.on(this.searchLinkButton, CLICK_EVENT, (event) => {
				event.preventDefault();
				this.openSearchModal();
			});
		}
	}

	selectOption(option) {
		if (!option) {
			return;
		}

		const nextValue = buildOptionValue(option, this.data);
		const answerField = this.data?.answerField || this.data?.valueField || 'value';
		const currentId = this.value?.[answerField] ?? this.value?.value;
		const nextId = nextValue?.[answerField] ?? nextValue?.value;

		// Avoid re-entrant onChange / dependent-question work for the same selection.
		if (String(currentId) === String(nextId) && this.value?.fieldName === nextValue?.fieldName) {
			return;
		}

		this.value = nextValue;
		if (this.value) {
			this.dependentQuestion = this.value.dependentQuestion;
		}
	}

	/**
	 * When there is exactly one venue, treat it as selected (for display + FormGenerator answers).
	 * Uses the value setter so onChange listeners sync before submit validation reads `answers`.
	 */
	ensureSingleVenueSelected() {
		if (this.options.length !== 1) {
			return this.value;
		}

		const nextValue = buildOptionValue(this.options[0], this.data);
		this.value = nextValue;
		if (this.value) {
			this.dependentQuestion = this.value.dependentQuestion;
		}
		return this.value;
	}

	radioButtonClickHandler() {
		this.selectedRadio = this.radioButtonElements.find((radio) => radio.checked);
		const selectedOption = this.options.find((option) => String(option.id) === String(this.selectedRadio?.value));
		if (selectedOption) {
			this.validate();
			this.selectOption(selectedOption);
		}
	}

	validate() {
		if (this.data?.isOptional) {
			return true;
		}

		const formElementData = {
			element: this.radioButtonContainerElement,
			formElement: this.radioButtonElements,
			errorMessage: this.data.errorMessage || 'Please select your venue',
			inputType: InputTypes.SELECTOR_RADIO,
			hasIcon: false
		};

		if (!this.isMultiSelect) {
			const selected = this.ensureSingleVenueSelected();
			return validFormElementValue(selected?.value || this.options[0]?.id || null, formElementData);
		}

		return validFormElementValue(this.selectedRadio?.value ?? this.value?.value, formElementData);
	}

	openSearchModal() {
		const modalSelector = this.data?.searchModalSelector || DEFAULT_SEARCH_MODAL_SELECTOR;
		this.searchModalComponent = SelectorEngine.findOne(modalSelector);

		if (!this.searchModalComponent?.modalEl) {
			// eslint-disable-next-line no-console
			console.error('VenueSelection: search modal not found', modalSelector);
			return;
		}

		if (!this.searchModalInstance) {
			this.searchModalInstance = new Modal(this.searchModalComponent.modalEl, { backdrop: 'static' });
			this.searchModalInstance.isEscapeEventEnable = false;
		}

		this.searchFormGenerator?.unload();
		this.searchFormGenerator = null;
		this.searchModalComponent.contentEl.innerHTML = '';
		this.searchModalComponent.contentEl.classList.remove('lta-venue-selection-search--success');
		this.searchModalComponent.contentEl.classList.add('lta-venue-selection-search');
		this.searchModalInstance.show();
		this.initiateSearchFormGenerator();
	}

	closeSearchModal() {
		this.searchFormGenerator?.unload();
		this.searchFormGenerator = null;
		this.searchModalComponent?.contentEl?.classList.remove('lta-venue-selection-search--success');
		this.searchModalInstance?.hide();
		this.containerEl?.dispatchEvent(
			new CustomEvent(VENUE_SELECTION_EXIT_EVENT, {
				bubbles: true,
				composed: true
			})
		);
	}

	getSearchFormQuestions() {
		return [
			{
				id: 1,
				step: 1,
				default: true,
				fieldName: 'venue',
				question: this.data?.searchQuestion || DEFAULT_SEARCH_QUESTION,
				questionPrefix: '',
				errorMessage: this.data?.searchErrorMessage || DEFAULT_SEARCH_ERROR_MESSAGE,
				questionType: VenueRegistrationQuestionTypes.VenueRegistrationVenueSearch,
				visible: true,
				dependentQuestion: [],
				label: this.data?.searchLabel || DEFAULT_SEARCH_LABEL,
				placeHolder: this.data?.searchPlaceHolder || DEFAULT_SEARCH_PLACEHOLDER,
				manualAddressLabel: this.data?.manualAddressLabel || DEFAULT_MANUAL_ADDRESS_LABEL,
				manualEntryTitle: this.data?.manualEntryTitle || DEFAULT_MANUAL_ENTRY_TITLE,
				manualEntryNote: this.data?.manualEntryNote || {
					title: DEFAULT_MANUAL_ENTRY_NOTE_TITLE,
					description: DEFAULT_MANUAL_ENTRY_NOTE_DESCRIPTION,
					backLinkLabel: DEFAULT_MANUAL_ENTRY_NOTE_BACK_LABEL
				},
				searchAgainLabel: this.data?.searchAgainLabel || DEFAULT_SEARCH_AGAIN_LABEL,
				searchAgainIcon: this.data?.searchAgainIcon || DEFAULT_SEARCH_AGAIN_ICON,
				webAdminErrorMessage: this.data?.webAdminErrorMessage || DEFAULT_WEB_ADMIN_ERROR_MESSAGE,
				webAdminSupportLinkLabel: this.data?.webAdminSupportLinkLabel || DEFAULT_WEB_ADMIN_SUPPORT_LINK_LABEL,
				webAdminSupportLinkUrl: this.data?.webAdminSupportLinkUrl || DEFAULT_WEB_ADMIN_SUPPORT_LINK_URL,
				onSelectHandler: this.searchSelectHandler,
				onSubmitDisabledChange: this.setSearchSubmitDisabled.bind(this),
				hasLimit: true,
				charLimit: 3,
				minSearchChar: 3
			}
		];
	}

	/**
	 * Used by VenueRegistration OnBoarding to run CheckIfWebAdmin (or similar)
	 * once after a search result is selected, before the blue address card shows.
	 */
	setSearchSelectHandler(handler) {
		this.searchSelectHandler = handler;
	}

	/**
	 * Used by VenueRegistration OnBoarding to POST the confirmed venue (venueSubmit)
	 * when the user clicks "Confirm this Venue" after selecting from search.
	 * @param {(venue: Object, options?: { redirect?: boolean }) => Promise<boolean>} handler
	 */
	setConfirmVenueSubmitHandler(handler) {
		this.confirmVenueSubmitHandler = handler;
	}

	/**
	 * Opt-in override for the "Save and exit" button (e.g. VenueRegistration OnBoarding
	 * redirects to the household account home page instead of closing the search modal).
	 * @param {string} url
	 */
	setSearchExitRedirectUrl(url) {
		this.searchExitRedirectUrl = url;
	}

	handleSearchExit() {
		if (this.searchExitRedirectUrl) {
			window.location.href = this.searchExitRedirectUrl;
			return;
		}

		this.closeSearchModal();
	}

	setSearchSubmitDisabled(isDisabled) {
		const submitButton = SelectorEngine.findOne(
			'.js-form-generator-next-button',
			this.searchFormGenerator?.containerEl || this.searchModalComponent?.contentEl
		);
		if (submitButton) {
			submitButton.disabled = !!isDisabled;
		}
	}

	initiateSearchFormGenerator() {
		if (!this.searchModalComponent?.contentEl) {
			return;
		}

		const questions = this.getSearchFormQuestions();

		this.searchFormGenerator = new FormGenerator(this.searchModalComponent.contentEl, {
			questions,
			totalSteps: 1,
			formType: FormTypes.SectionForm,
			themeColor: 'color-lta-blue',
			hasSeparator: false,
			isHideProgress: true,
			isBackButtonEnabled: true,
			isExitButtonEnabled: true,
			exitButtonLabel: 'Exit',
			exitButtonClasses: 'lta-form-generator__form-exit-button--underlined',
			exitButtonHandler: () => this.handleSearchExit(),
			backButtonHandlerFirstStep: () => this.closeSearchModal(),
			nextStepButtonLabel: 'Confirm this Venue',
			showExitWithoutSavingButton: true,
			exitWithoutSavingButtonLabel: 'Exit without saving',
			exitWithoutSavingButtonHandler: () => this.closeSearchModal(),
			questionRenderers: venueRegistrationQuestionRenderers,
			submitButton: {
				title: 'Confirm this Venue',
				alwaysVisible: true
			}
		});

		this.searchFormGenerator.onSubmit(() => {
			this.handleSearchVenueSubmit();
		});

		this.revealSearchHeading();
	}

	getAccessRequestSuccessContent() {
		if (this.data?.accessRequestSuccessContent) {
			return this.data.accessRequestSuccessContent;
		}

		const cardTitle = this.data?.accessRequestSuccessCardTitle || 'What happens next?';
		const cardHtml =
			this.data?.accessRequestSuccessCardHtml ||
			`
				<ul class="u-pl-3 u-mb-0">
					<li class="u-body1 u-text-grey-lta u-mb-2">An email has been sent to this venue requesting them to add you as a role holder to this venue. They have 72 hours to accept before the invitation expires.</li>
					<li class="u-body1 u-text-grey-lta u-mb-2">If they <strong>accept</strong> you will be notified and you can continue with the venue registration.</li>
					<li class="u-body1 u-text-grey-lta u-mb-0">If they <strong>decline</strong>, you will also be notified and you will not be able to complete any information on behalf of this venue.</li>
				</ul>
			`;

		return `
			<div class="u-bg-white u-rounded u-p-3">
				<h2 class="u-subtitle3 u-fw-bold u-mb-2">${cardTitle}</h2>
				${cardHtml}
			</div>
		`;
	}

	showAccessRequestSuccessScreen() {
		if (!this.searchModalComponent?.contentEl) {
			return;
		}

		this.searchFormGenerator?.unload();
		this.searchFormGenerator = null;

		const host = this.searchModalComponent.contentEl;
		const title = this.data?.accessRequestSuccessTitle || "You've submitted a request to access this venue";
		const description =
			this.data?.accessRequestSuccessDescription || 'This will be sent to the admin at your chosen venue.';
		const eyebrow = this.data?.accessRequestSuccessEyebrow || 'Congratulations';
		const buttonLabel = this.data?.accessRequestSuccessButtonLabel || 'Close';

		host.classList.add('lta-venue-selection-search--success');
		host.innerHTML = `
			<lta-success-screen
				eyebrow="${eyebrow}"
				title="${title}"
				description="${description}"
				button-label="${buttonLabel}"
			>
				${this.getAccessRequestSuccessContent()}
			</lta-success-screen>
		`;

		const successScreen = SelectorEngine.findOne('lta-success-screen', host);
		if (successScreen) {
			EventHandler.on(successScreen, 'lta-success-screen:close', () => {
				this.closeSearchModal();
			});
		}
	}

	revealSearchHeading() {
		const venueFinder =
			SelectorEngine.findOne('.lta-park-finder', this.searchModalComponent.contentEl) ||
			SelectorEngine.findOne('.lta-school-finder', this.searchModalComponent.contentEl);
		const title =
			SelectorEngine.findOne('.lta-park-finder-title', venueFinder) ||
			SelectorEngine.findOne('.lta-school-finder-title', venueFinder);
		if (!title) {
			return;
		}

		// Show the FormComponent heading once; SearchInputField's nested title is hidden via CSS.
		title.classList.remove(ClassNames.Hidden);
	}

	async handleSearchSaveAndExit() {
		if (!this.searchFormGenerator) {
			return;
		}

		const isValid = await this.searchFormGenerator.formValidation();
		if (!isValid) {
			this.searchFormGenerator.enableSubmitButton();
			return;
		}

		this.handleSearchVenueSubmit();
	}

	/**
	 * Autosuggest / manual entry: venueSubmit when wired.
	 * Manual address entry: then show access-request success screen.
	 */
	async handleSearchVenueSubmit() {
		const searchAnswer = this.extractSearchVenueAnswer();
		this.applySearchedVenue(searchAnswer);

		const isManual = this.isManualVenueEntry(searchAnswer);
		const venue =
			(searchAnswer?.value && typeof searchAnswer.value === 'object' ? searchAnswer.value : null) ||
			this.value ||
			mapSearchResultToOption(searchAnswer);

		if (this.confirmVenueSubmitHandler && venue) {
			const submitted = await this.confirmVenueSubmitHandler(venue, { redirect: !isManual });
			if (!submitted) {
				this.searchFormGenerator?.enableSubmitButton();
				return;
			}

			if (isManual) {
				this.showAccessRequestSuccessScreen();
			}
			return;
		}

		if (isManual) {
			this.showAccessRequestSuccessScreen();
			return;
		}

		this.closeSearchModal();
	}

	isManualVenueEntry(searchAnswer = {}) {
		const venueValue =
			searchAnswer?.value && typeof searchAnswer.value === 'object' ? searchAnswer.value : searchAnswer;
		return !venueValue?.id;
	}

	extractSearchVenueAnswer() {
		const answers = this.searchFormGenerator?.answers || {};
		for (const stepKey of Object.keys(answers)) {
			const stepAnswers = answers[stepKey] || {};
			for (const questionKey of Object.keys(stepAnswers)) {
				const answer = stepAnswers[questionKey];
				if (answer?.value) {
					return answer;
				}
			}
		}

		const searchQuestion = this.searchFormGenerator?.questions?.find(
			(question) => question.questionType === VenueRegistrationQuestionTypes.VenueRegistrationVenueSearch
		);
		return searchQuestion?.ref?.value || null;
	}

	applySearchedVenue(searchAnswer) {
		const option = mapSearchResultToOption(searchAnswer);
		if (!option?.id) {
			return;
		}

		const existingIndex = this.options.findIndex((item) => String(item.id) === String(option.id));
		const nextOptions =
			existingIndex >= 0
				? this.options.map((item, index) => (index === existingIndex ? { ...item, ...option } : item))
				: [...this.options, option];

		this.selectOption(option);
		this.updateData({ options: nextOptions });
	}
}

export default VenueSelection;
